<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-title>{{ $t('permission.title') }}</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">
      <!-- No notification -->
      <div v-if="!notification" class="ion-padding ion-text-center">
        <p>{{ $t('permission.noNotification') }}</p>
        <ion-button @click="dismiss" fill="outline">{{ $t('common.back') }}</ion-button>
      </div>

      <!-- Expired -->
      <div v-else-if="isExpired" class="ion-padding ion-text-center">
        <p>{{ $t('permission.expired', {validUntilTime: validUntilTime}) }}</p>
        <ion-button @click="dismiss" fill="outline">{{ $t('common.back') }}</ion-button>
      </div>

      <!-- Loaded -->
      <div v-else class="ion-padding">
        <div class="message-card">
          <p>{{ $t('permission.title') }}</p>
          <p v-if="validUntilTime" class="expires-on">{{
              $t('permission.validUntil', {validUntilTime: validUntilTime})
            }}</p>
        </div>

        <div class="message-card">
          <p class="message-text">{{ notification.message }}</p>
        </div>

        <ion-grid>
          <ion-row>
            <ion-col>
              <ion-button v-if="notification.biometricsRequired" @click="handleApproveWithBiometrics" expand="block"
                          color="primary">
                {{ $t('biometric.modal.approve') }}
              </ion-button>
              <ion-button v-else @click="handleApprove" expand="block" color="secondary">
                {{ $t('common.ok') }}
              </ion-button>
            </ion-col>
            <ion-col>
              <ion-button @click="handleClickNo" expand="block" fill="outline" color="medium">
                {{ $t('common.cancel') }}
              </ion-button>
            </ion-col>
          </ion-row>
        </ion-grid>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {IonButton, IonCol, IonContent, IonGrid, IonHeader, IonPage, IonRow, IonTitle, IonToolbar,} from '@ionic/vue';
import {computed, watch} from 'vue';
import {clearActiveNotification, notificationState} from '@/stores/notificationStore';
import {useBiometric} from '@/composables/useBiometric';
import {toast} from '@/utils/notifications.ts';
import {PermissionService} from '@/services/PermissionService.ts';
import {useI18n} from "vue-i18n";
import {useNavigation} from "@/composables/useNavigation.ts";

const {goBack} = useNavigation();
const {t} = useI18n();
const {silentAuthenticate} = useBiometric();

const notification = computed(() => notificationState.activeNotification);
const validUntil = computed(() => notification.value?.expiresAt ?? null);
const isExpired = computed(() => validUntil.value ? new Date(validUntil.value) < new Date() : false);

const validUntilTime = computed(() => {
  if (!validUntil.value) return null;
  const date = new Date(validUntil.value);
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: false});
});

const dismiss = async () => {
  await clearActiveNotification();
  goBack();
}

const submitPermission = async (isApproved: boolean) => {
  if (notification.value?.notificationId) {
    await PermissionService.permissionApproval(notification.value.notificationId, isApproved);
  }
};

const handleApproveWithBiometrics = async () => {
  await silentAuthenticate(
      async () => {
        await submitPermission(true);
      },
      {
        onSuccess: async () => {
          await toast({ message: t('permission.success'), preset: 'success' });
          await dismiss();
        },
        onError: async (err: any) => {
          if (err.isBiometricError) {
            // stay on page, show toast
            await toast({ message: t('biometric.errors.failed'), preset: 'error' });
          } else {
            // server/network error — leave
            const msg = err?.error === 'timeout' ? t('permission.timeout') : t('permission.authFailed');
            await toast({ message: msg, preset: 'error' });
            await dismiss();
          }
        }
      },
  );
};

const handleDecision = async (isApproved: boolean) => {
  try {
    await submitPermission(isApproved);
    await toast({ message: t('permission.success'), preset: 'success' });
    await dismiss();
  } catch (err: any) {
    const msg = err?.error === 'timeout' ? t('permission.timeout') : t('permission.error');
    await toast({message: msg, preset: 'error'});
    await dismiss();
  }
};

const handleApprove = () => handleDecision(true);
const handleClickNo = () => handleDecision(false);
</script>